import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import ReactTable from "src/components/tables/react-table/ReactTable";
import { handleCsvExport } from "src/helpers/helpers";
import useDebounce from "src/hooks/useDebounce";
import useManualRefresh from "src/hooks/useManualRefresh";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import {
  useDeleteMembership,
  useGetMemberships,
  useUpdateMembershipeStatus,
} from "../api-clients";
import useMembershipTableColumns from "./useMembershipTableColumns";

const MembershipList = () => {
  useSetActiveTab(TAB_NAMES.MEMBERSHIP_MANAGEMENT);
  const navigate = useNavigate();
  const [length, setLength] = useState(10);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText);
  const { successNotification, errorNotification } = useNotification();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [idToOperate, setIdToOperate] = useState<number | undefined>(undefined);
  const {
    isLoading,
    error,
    data,
    mutate: updateMembershipData,
    isValidating,
  } = useGetMemberships({ length, page, search: debouncedSearch });

  const { isRefreshing, handleRefresh } =
    useManualRefresh(updateMembershipData);

  const {
    isLoading: isDeleteLoading,
    isSuccess: isDeleteSucceed,
    error: deleteErr,
    execute: deleteMembership,
  } = useDeleteMembership();

  const membershipsData = useMemo(() => data?.data?.rows ?? [], [data?.data]);

  const totalRecords = useMemo(() => data?.data?.count, [data?.data?.count]);

  const {
    execute: handleMembershipStatusChange,
    isSuccess: isStatusUpdateSuccess,
  } = useUpdateMembershipeStatus();

  const columns = useMembershipTableColumns({
    onDelete: (id: number) => {
      setIdToOperate(id);
      setIsDeleteModalOpen(true);
    },
    handleMembershipStatusChange,
  });

  useEffect(() => {
    if (isDeleteSucceed) {
      updateMembershipData();
      setIsDeleteModalOpen(false);
      successNotification("Membership Deleted.");
    }
  }, [isDeleteSucceed, successNotification, updateMembershipData]);

  useEffect(() => {
    if (deleteErr) {
      errorNotification();
    }
  }, [deleteErr, errorNotification]);

  useEffect(() => {
    if (isStatusUpdateSuccess) {
      updateMembershipData();
    }
  }, [isStatusUpdateSuccess, updateMembershipData]);

  return (
    <>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        isLoading={isDeleteLoading}
        onSubmit={() => deleteMembership(idToOperate)}
      />
      <div className="flex flex-col gap-[20px]">
        <TabHeader
          label="Membership Management"
          onRefresh={() => handleRefresh()}
          exportButton={{
            label: "Export",
            onClick: () => handleCsvExport(membershipsData),
          }}
          printButton={{
            label: "Print",
            onClick: () => window.print(),
          }}
          createButton={{
            label: "Add Membership",
            onClick: () => navigate("create"),
          }}
          search={{
            onChange: (val) => setSearchText(val),
            value: searchText,
          }}
        />
        <ReactTable
          columns={columns}
          data={membershipsData}
          isLoading={isLoading || isRefreshing}
          pagination={{
            setLength,
            length,
            total: totalRecords,
            page,
            setPage,
          }}
        />
      </div>
    </>
  );
};

export default MembershipList;
